import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { PaymentDescription } from '@/scopes/account/booking-payment/components/PaymentDescription'
import { PaymentHeader } from '@/scopes/account/booking-payment/components/PaymentHeader'
import { PaymentTitle } from '@/scopes/account/booking-payment/components/PaymentTitle'
import { useAccountBookingQueryParam, useAuthProtection } from '@/scopes/account/common/hooks'
import { Footer } from '@/scopes/common/components'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { Payment } from '@/scopes/payment/components/Payment'
import { getContextItemIdGroupBookingPaymentPage } from '@/scopes/payment/domain/groups/policies/contextItemId'
import { GroupBookingPaymentVariantValues } from '@/scopes/payment/domain/groups/types'
import { PaymentReactContext, PaymentStepProvider } from '@/scopes/payment/domain/PaymentContext'
import { getContextItemIdBookingPaymentPage } from '@/scopes/payment/domain/policies/contextItemId'
import { PaymentActionsDependenciesProvider } from '@/scopes/payment/infrastructure/dependencies'
import styles from './styles.module.css'

function PaymentPageContent() {
  const { isLoggedIn, isLoading } = useAuthProtection()
  const intl = useIntl()

  if (isLoading) {
    return (
      <BookingPaymentLayout>
        <PageLoader />
      </BookingPaymentLayout>
    )
  }

  if (!isLoggedIn) {
    return (
      <BookingPaymentLayout>
        <Typography>{intl.formatMessage({ id: 'payment_scope_booking_payment_page_not_authorized' })}</Typography>
      </BookingPaymentLayout>
    )
  }

  return (
    <BookingPaymentLayout>
      <PaymentTitle />
      <PaymentDescription />
      <Payment />
    </BookingPaymentLayout>
  )
}
interface BookingPaymentLayoutProps {
  children: React.ReactNode
}

function BookingPaymentLayout({ children }: BookingPaymentLayoutProps) {
  return (
    <div className={cn(styles.wrapper)}>
      <PaymentHeader />
      <main className={cn(styles.mainContent)} id="main-content" tabIndex={-1}>
        <div className={cn(styles.content)}>{children}</div>
      </main>
      <Footer className={cn(styles.footer)} />
    </div>
  )
}

function PageLoader() {
  return (
    <div className={styles.pageLoader}>
      <IndeterminateProgressCircle size={32} />
    </div>
  )
}

const queryClient = new QueryClient()

interface BookingPaymentPageContainerProps {
  contextItemId: string
}

function BookingPaymentPageContainer({ contextItemId }: BookingPaymentPageContainerProps) {
  const bookingNumber = useAccountBookingQueryParam('bookingNumber')
  const groupUserId = useAccountBookingQueryParam('groupUserId')
  const { isMobile } = useBreakpoints()

  const paymentContextValue = React.useMemo(
    () => ({
      contextItemId,
      groupUserId: groupUserId || null,
      scope: 'BookingPayment' as const,
      bookingNumber: bookingNumber ?? '',
    }),
    [contextItemId, groupUserId, bookingNumber],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <PaymentReactContext value={paymentContextValue}>
        <PaymentStepProvider isWizard={isMobile}>
          <PaymentActionsDependenciesProvider>
            <MetaTitle titleTranslationTag="payment_scope_booking_payment_page_meta_title" />
            <PaymentPageContent />
          </PaymentActionsDependenciesProvider>
        </PaymentStepProvider>
      </PaymentReactContext>
    </QueryClientProvider>
  )
}

export function BookingPaymentPage() {
  const siteName = useConfiguration('siteName')
  const contextItemId = getContextItemIdBookingPaymentPage(siteName)

  return <BookingPaymentPageContainer contextItemId={contextItemId} />
}

export function GroupsWinterBookingPaymentPage() {
  const siteName = useConfiguration('siteName')
  const contextItemId = getContextItemIdGroupBookingPaymentPage(siteName, GroupBookingPaymentVariantValues.WINTER)

  return <BookingPaymentPageContainer contextItemId={contextItemId} />
}

export function GroupsTotallySnowBookingPaymentPage() {
  const siteName = useConfiguration('siteName')
  const contextItemId = getContextItemIdGroupBookingPaymentPage(siteName, GroupBookingPaymentVariantValues.TOTALLY_SNOW)

  return <BookingPaymentPageContainer contextItemId={contextItemId} />
}
